import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { MultilineInput } from "@Components/Input/MutlilineInput"
import { Spacer } from "@Components/Space"
import { baseColors, baseFontFamily } from "@Constants/baseStyles"
import MultiSlider from "@ptomasroos/react-native-multi-slider"
import { currentDiagnosisAtom } from "@Recoil/Atom/DiagnosisAtom"
import { canGotoNextAtom, surveyResponsesAtom } from "@Recoil/Atom/surveyAtom"
import React, { useEffect, useState } from "react"
import { Dimensions, StyleSheet, View } from "react-native"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ParentSurveyContent } from "./ParentSurveyContent"
import { SALUTANCE } from "@Services/AxiosInstance"

const { width } = Dimensions.get("screen")

export const QuestionWithScale = (props) => {
    const {
        axisId,
        surveyId,
        responseTypeId,
        hasPrecision,
        question,
        color,
        isChildren,
        questionNumber,
        userResponse,
        disabled,
        isParent,
        precisionText
    } = props

    const defaultResponse = {
        axisId: axisId,
        surveyId: surveyId,
        responseTypeId: responseTypeId,
        responseValue: 0,
        hasPrecision: hasPrecision,
        precisionAnswer: ""
    }

    const [response, setResponse] = useState(defaultResponse)
    const [responseGlobal, setResponseGlobal] = useRecoilState(surveyResponsesAtom)
    const [sliderValue, setSliderValue] = useState(response.responseValue ?? 0)
    const [precision, setPrecision] = useState("")
    const [isDragging, setIsDragging] = useState(false)
    const setCanGoToNext = useSetRecoilState(canGotoNextAtom)
    const currentEducationalDiagnosis = useRecoilValue(currentDiagnosisAtom)

    const saveResponse = (texValue) => {
        if (responseGlobal[surveyId]) {
            setResponseGlobal((old) => ({
                ...old,
                [surveyId]: { ...responseGlobal[surveyId], responseValue: texValue }
            }))
        } else {
            setResponseGlobal((old) => ({ ...old, [surveyId]: { ...response, responseValue: texValue } }))
        }
    }

    useEffect(() => {
        setCanGoToNext(true)
    }, [])

    useEffect(() => {
        if (responseGlobal[response?.surveyId]) {
            setResponse({ ...responseGlobal[response?.surveyId] })
            setSliderValue(responseGlobal[response?.surveyId].responseValue)
            setPrecision(responseGlobal[response?.surveyId].precisionAnswer)
        } else {
            setSliderValue(0)
            saveResponse(0)
        }
    }, [responseGlobal])

    useEffect(() => {
        if (userResponse?.id > 0 && currentEducationalDiagnosis?.id > 0) {
            setSliderValue(+userResponse?.response)
            setPrecision(userResponse?.precision)
        }
    }, [userResponse])

    const handleChangeSlide = (texValue) => {
        const rounded = Math.round(texValue)
        setSliderValue(rounded)
        saveResponse(rounded)
    }

    const handleChangePrecision = (texValue) => {
        setPrecision(texValue)
        if (responseGlobal[surveyId]) {
            setResponseGlobal((old) => ({
                ...old,
                [surveyId]: { ...responseGlobal[surveyId], precisionAnswer: texValue }
            }))
        } else {
            setResponseGlobal((old) => ({ ...old, [surveyId]: { ...response, precisionAnswer: texValue } }))
        }
    }

    if (isParent) {
        return <ParentSurveyContent question={question} />
    }

    return (
        <View style={{ padding: 20 }}>
            <OpenSansText color={color}>
                {SALUTANCE ? "QUESTION : " : !isChildren ? `QUESTION N° ${questionNumber}` : ""}
            </OpenSansText>

            <OpenSansText style={{ marginTop: 10 }} fontSize={14}>
                {question}
            </OpenSansText>

            <View
                style={{
                    marginTop: 20,
                    marginHorizontal: 10,
                    paddingHorizontal: 10,
                    justifyContent: "center",
                    alignItems: "center"
                }}
            >
                <MultiSlider
                    snapped
                    onValuesChangeStart={() => setIsDragging(true)}
                    onValuesChangeFinish={() => setIsDragging(false)}
                    onValuesChange={(value) => handleChangeSlide(value[0])}
                    values={[sliderValue]}
                    touchDimensions={styles.touchDimensions}
                    selectedStyle={{ backgroundColor: baseColors.green.dark }}
                    trackStyle={{
                        backgroundColor: baseColors.green.pastel,
                        height: 5,
                        borderRadius: 5
                    }}
                    markerStyle={{
                        borderWidth: 7,
                        borderColor: baseColors.green.dark
                    }}
                    minMarkerOverlapStepDistance={0}
                    minMarkerOverlapDistance={0}
                    customMarker={(e) => (
                        <View
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                paddingTop: e.currentValue > 0 && Math.round(e.currentValue) < 10 ? 25 : 5,
                            }}
                        >
                            <View
                                style={{
                                    height: 25,
                                    width: 25,
                                    borderRadius: 15,
                                    backgroundColor: baseColors.white,
                                    borderColor: baseColors.green.primary,
                                    borderWidth: 5,
                                }}
                            />
                            {e.currentValue > 0 && Math.round(e.currentValue) < 10 && (
                                <OpenSansText color={baseColors.black} style={{ textAlign: "center", fontSize: 16 }}>
                                    {Math.round(e.currentValue)}
                                </OpenSansText>
                            )}
                        </View>
                    )}
                    sliderLength={width / 2 + 100}
                    min={0}
                    max={10}
                    step={1}
                />

                <View style={styles.valuesContainer}>
                    <View
                        style={{
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            alignContent: "flex-start",
                            backgroundColor: "transparent"
                        }}
                    >
                        <OpenSansText fontSize={16}>0</OpenSansText>
                        <OpenSansText
                            style={{
                                textAlign: "center",
                                fontFamily: baseFontFamily.OpenSansBold,
                                marginTop: 5,
                                left: -10
                            }}
                            fontSize={12}
                        >
                            Pas du tout
                        </OpenSansText>
                    </View>

                    <View
                        style={{
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                            flex: 0.6
                        }}
                    >
                        <OpenSansText style={{}} fontSize={16}>
                            10
                        </OpenSansText>
                        <OpenSansText
                            style={{
                                textAlign: "center",
                                fontFamily: baseFontFamily.OpenSansBold,
                                marginTop: 5,
                                right: -10
                            }}
                            fontSize={12}
                        >
                            Le plus que vous puissiez imaginer
                        </OpenSansText>
                    </View>
                </View>
            </View>
            {Boolean(hasPrecision) && (
                <>
                    <Spacer height={20} />
                    {/* <OpenSansText fontSize={14}>Apportez des précisions si vous le souhaitez :</OpenSansText> */}
                    <OpenSansText fontSize={14}>
                        {precisionText ?? "Apportez des précisions si vous le souhaitez : "}
                    </OpenSansText>
                    <Spacer height={20} />
                    <MultilineInput
                        disabled={disabled}
                        height={80}
                        value={precision}
                        onChange={(text) => handleChangePrecision(text)}
                    />
                </>
            )}

            <Spacer />
        </View>
    )
}

const styles = StyleSheet.create({
    touchDimensions: {
        height: 25,
        width: 25,
        borderRadius: 10
    },
    valuesContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        // alignItems: "center",
        marginTop: -12.8,
        width: width / 2 + 100
    }
})
